import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Remove product import — phased validation (see {@link BaseImportTransformer} run contract).
 * 1) Row / cell pre-checks (missing part number)
 * 2) Batched part id resolution (cached; HTTP skipped when there are no part numbers; never aborts the run)
 * 3) Row-level unknown part
 *
 * Preview (`!isFinalRun`): returns all rows as apiRequests placeholders where needed + merged `preventativeIssues`.
 * Final (`isFinalRun`): `apiRequests[i]` is `null` for any row with a preventative issue; otherwise `{ partId }`.
 */
export class RemoveProductImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 2: batched part lookup (per-part cache; HTTP only for uncached part numbers) ─
        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        // ── Phase 3: API resolution (unknown part) ──────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Emit merged issues (one row → one entry, deduped codes) ──────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        // ── Build apiRequests (same length as `rows`) ────────────────────────────
        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partId: null, partNumber: undefined};
            }

            const rs = rowState[index];
            if (!rs.partNoUpper) {
                return isFinalRun ? null : {partId: null, partNumber: item.partNumber};
            }

            const partId = partIds[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(index);

            if (hasIssue) {
                return isFinalRun
                    ? null
                    : {partId: partId ?? null, partNumber: item.partNumber};
            }

            return {partId};
        });

        return {apiRequests, preventativeIssues};
    }
}
